import EventEmitter from 'events'
import config from '../../config';
import LoginStore from '../../services/session/LoginStore';

/**
 * This socket protocol could be moved somewhere more common. Would need to be moved here,
 * and routes would need to be moved around on the dashboard server (i.e. not /chatter/socket)
 */


const nextId = (() => {
  let current = 0;
  return () => "" + (current += 1)
})();

const isDebugging = false;
const debug = (...args) => {
  if (isDebugging) console.info(...args)
}

let emitter = new EventEmitter();
emitter.setMaxListeners(500);
let queue = [];
let token = null;
let ws = null;
let lastConnect = 0;
let readyPromise = null;

const states = {
  UNINITIALIZED: 'UNINITIALIZED',
  CONNECTING: 'CONNECTING',
  AUTHENTICATING: 'AUTHENTICATING',
  CONNECTED: 'CONNECTED'
};
let state = states.UNINITIALIZED;
const STATE_CHANGE = 'STATE_CHANGE';

function setState(newState) {
  debug('ws state', newState);
  const oldState = state;
  state = newState;
  emitter.emit(STATE_CHANGE, { oldState, newState })
}

function send(path, body, messageId) {
  debug(`→ (${messageId}) ${path}`, body)

  ws.send(JSON.stringify({
    path, body, messageId
  }))
}


function initialize() {
  lastConnect = (new Date()).getMilliseconds();
  setState(states.CONNECTING);
  const host = config.apiSocketUrl;
  try {
    ws = ws || new WebSocket(`${host}/chatter/socket`);
  } catch (e) {
    throw e
  }
  ws.onclose = ((err) => {
    console.error('websocket connect failed', err);
    const { reason, code, wasClean } = err;
  });

  token = LoginStore.token;

  readyPromise = new Promise((resolve, reject) => {
    ws.onopen = () => {
      setState(states.AUTHENTICATING);
      send('/authenticate', {token: token}, 'authenticate');
      emitter.once(
        'responseTo:authenticate',
        (response) => {
          setState(states.CONNECTED);
          dequeue();
          resolve();
        });
      ws.onclose = ({ reason, code, wasClean }) => {
        ws = null;
        if (LoginStore.token === token) {
          const throttle = (lastConnect + 5000) - (new Date()).getMilliseconds()
          debug('closed', code, reason, wasClean);
          setState(states.UNINITIALIZED);
          setTimeout(() => initialize(), throttle)

        }
      };
      ws.onmessage = ({ data }) => {
        const response = JSON.parse(data);
        if (response.statusCode >= 400) {
          console.error('\t', `← (${response.responseTo}) ${response.path}`, response.statusCode, response.body);
        } else {
          debug('\t', `← (${response.responseTo}) ${response.path}`, response.statusCode, response.body);
        }
        emitter.emit(response.path, response);
        if (response.responseTo) {
          emitter.emit('responseTo:' + response.responseTo, response)
        }
      }
    };
  })
}

function dequeue() {
  if (queue.length === 0 || state !== states.CONNECTED) return;
  const { path, body, messageId } = queue.shift();
  send(path, body, messageId);
  dequeue();
}

let self = {

  onReady() {
    if (state === states.CONNECTED) {
      return Promise.resolve(true)
    } else if (state === states.CONNECTING || state === states.AUTHENTICATING) {
      return readyPromise
    } else if (state === states.UNINITIALIZED) {
      initialize()
      return readyPromise
    }
  },

  send(path, body) {
    // TODO initialize on login.
    // Also, this will be broken if user logs
    // out and back in as another user
    if (state === states.UNINITIALIZED) initialize();

    const messageId = nextId();
    if (state !== states.CONNECTED) {
      queue.push({path, body, messageId})
    } else {
      send(path, body, messageId)
    }
    return messageId
  },

  on(msg, handler) {
    return emitter.on(msg, handler)
  },

  once(msg, handler) {
    return emitter.once(msg, handler)
  },

  off(msg, handler) {
    return emitter.removeListener(msg, handler)
  },

  onStateChange(handler) {
    emitter.on(STATE_CHANGE, handler);
    return () => {
      emitter.removeListener(STATE_CHANGE, handler)
    }
  },

  sendExpectingResponse(path, body) {
    const id = self.send(path, body)
    return new Promise((resolve, reject) => {
      emitter.once('responseTo:' + id, (response) => {
        resolve(response)
      })
    })
  },

  // TODO - This reconnect logic is probz not going to be sufficient.
  // There's larger state questions that need to be acknowledged,
  // and the streams being sent should probably be handled that way
  //
  // Example:
  // - Establish a stream without a filter (would push a state of 'want stream')
  // - then add a filter (would push some sort of filter state)
  // - Socket disconnects
  // --- The current logic on this method would just reconnect the stream, and tweets would be unfiltered
  // --- but the front end still thinks a filter is applied

  // A solution could be so that all filter related
  // state is passed through the body, and this method would have to remove previous stream handlers
  // would probably want to encapsulate that logic in a single object, that can keep filter state internal


  /**
   *
   * @param {string} path - path to the stream handler endpoint usually /some/path/stream
   * @param {string} offPath - path to the off switch for the stream usually /some/path/stopStream
   * @param {object} body - optional body (for filters, or other stream state
   * @param {function(object):void} handler
   * @returns {function(): void} - callback to disable handler
   */
  stream: ((path, offPath, body) => handler => {

    let id = null;
    function removeResponseListener() {
      emitter.removeListener('responseTo:' + id, handler);
    }

    function connect({newState: state}) {
      if (state === states.CONNECTED) {
        removeResponseListener();
        id = self.send(path, body);
        emitter.on('responseTo:' + id, handler);
      }
    }

    emitter.on(STATE_CHANGE, connect); // reestablish connection

    connect({ newState: state}); // actually connect

    return function off() {
      removeResponseListener();
      self.send(offPath); // hit the off switch on the server so messages stop getting sent in
      emitter.removeListener(STATE_CHANGE, connect);
    }

  }),

  states
};

export default self
